/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: 视频翻译功能特性区块（复用自 AI Avatar 的 PowerfulFeaturesSection）
 */
import Image from 'next/image';
import React from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';

import styles from './index.module.scss';

export interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  /** 可选的独立 CTA 文本，不传则使用全局 ctaText */
  ctaText?: string;
  /** 可选的独立跳转链接，不传则使用全局 ctaUrl */
  ctaUrl?: string;
}

export interface FeaturesSectionProps {
  /** 区块标题 */
  title: string;
  /** 区块副标题 */
  subtitle: string;
  /** 功能列表 */
  features: FeatureItem[];
  /** CTA 按钮文本 */
  ctaText: string;
  /** 跳转链接，默认 VIDEO_TRANSLATOR_URL */
  ctaUrl?: string;
  /** GTM element-name 基础值，各按钮自动追加 -{index+1} 序号 */
  elementNameBase?: string;
}

interface FeatureItemComponentProps {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
  buttonText: string;
  buttonClassName?: string;
  buttonHref: string;
  buttonElementName?: string;
}

const FeatureItemComponent: React.FC<FeatureItemComponentProps> = ({
  title,
  description,
  imageUrl,
  imageAlt,
  reverse = false,
  buttonText,
  buttonClassName,
  buttonHref,
  buttonElementName,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton
          text={buttonText}
          href={buttonHref}
          className={buttonClassName}
          elementName={buttonElementName}
        />
      </div>
    </div>
  );
};

const FeaturesSection: React.FC<FeaturesSectionProps> = ({
  title,
  subtitle,
  features,
  ctaText,
  ctaUrl,
  elementNameBase,
}) => {
  const targetUrl = ctaUrl || VIDEO_TRANSLATOR_URL;

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          {title}
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          {subtitle}
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItemComponent
              key={index}
              title={feature.title}
              description={feature.description}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
              buttonText={feature.ctaText || ctaText}
              buttonClassName={styles.featureButton}
              buttonHref={feature.ctaUrl || targetUrl}
              buttonElementName={
                elementNameBase ? `${elementNameBase}-${index + 1}` : undefined
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
